// Productfoto-varianten — de pure kern.
//
// Waarom: in productie bestaat /_next/image niet (404, zie commit 73c9f9b), dus next/image op de
// productkaarten laadt de volledige originele foto (1200x1600, 50-250 KB) rechtstreeks uit Firebase
// Storage, 30 stuks per overzichtspagina. Deze kern beschrijft de kleine webp-varianten die naast
// elk origineel in Storage staan (breedte 384, 640 en 960) en bouwt daar de srcset uit.
//
// Geen imports en alleen "wegstripbare" TypeScript, zodat Node 26 dit bestand rechtstreeks laadt
// (scripts/backfill-product-image-variants.js en scripts/product-image-variants.test.mjs) en de
// client-bundel er niets extra's van meekrijgt (zelfde patroon als lib/blog-core.ts).
//
// Opslag: `products/<map>/<uuid>.webp` heeft varianten `products/<map>/<uuid>.w384.webp`,
// `.w640.webp` en `.w960.webp`. De naam is af te leiden uit de URL van het origineel, dus er komt
// NIETS in de productdocumenten (geen nieuw veld, dus ook geen whitelist in de admin-API). De
// varianten worden zonder download-token gelezen: storage.rules staat lezen voor iedereen toe.
//
// "Alleen varianten die echt bestaan": de set is per foto altijd compleet of afwezig. Compleet
// wordt hij gemaakt door (1) /api/admin/upload en /api/admin/upload/recrop bij elke nieuwe
// productfoto (lib/product-image-variants-server.ts) en (2) scripts/backfill-product-image-variants.js
// voor alles wat er al stond (`--check` controleert, zonder te schrijven). Valt er toch eentje weg,
// dan schakelt ProductCard bij de laadfout terug op het origineel.

/** Breedtes (px) van de varianten, klein naar groot. Elke foto krijgt ze allemaal (zie hieronder). */
export const PRODUCT_VARIANT_BREEDTES = [384, 640, 960] as const;

/**
 * `sizes` van een productkaart: 2 kolommen op een telefoon, 3 op een tablet, 4 op een computer
 * (zelfde waarde als de kaart voorheen aan next/image meegaf).
 */
export const PRODUCT_KAART_SIZES = "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw";

/** Cache-Control van een variant: de naam bevat de uuid van het origineel en wordt nooit overschreven. */
export const PRODUCT_VARIANT_CACHE_CONTROL = "public, max-age=31536000, immutable";

/** Kwaliteit (webp) van de varianten; het origineel is q82 bij 1200x1600. */
export const PRODUCT_VARIANT_KWALITEIT = 78;

const STORAGE_HOST = "firebasestorage.googleapis.com";
// /v0/b/<bucket>/o/<object, percent-encoded>; de query (alt=media&token=...) interesseert ons niet.
const STORAGE_PAD = /^\/v0\/b\/([a-z0-9][a-z0-9._-]{2,220})\/o\/([^/?#]+)$/i;
// Alleen echte productfoto's: products/<map>/<naam>.<ext>, niet zelf al een variant.
const PRODUCT_OBJECT = /^products\/[^/]+\/[^/]+\.(?:webp|jpe?g|png)$/i;
const IS_VARIANT = /\.w\d{2,4}\.webp$/i;

export interface ProductFotoBron {
  bucket: string;
  /** Object-pad in de bucket, bijv. "products/slip/abc.webp". */
  objectPad: string;
}

/**
 * Een Firebase Storage-URL van een productfoto -> bucket en object-pad; null voor al het andere
 * (andere host, andere map, een variant, kapotte percent-codering).
 */
export function parseProductFotoUrl(url: string | undefined | null): ProductFotoBron | null {
  if (!url || typeof url !== "string") return null;
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  if (u.protocol !== "https:" || u.hostname !== STORAGE_HOST) return null;
  const m = STORAGE_PAD.exec(u.pathname);
  if (!m) return null;
  let objectPad: string;
  try {
    objectPad = decodeURIComponent(m[2]);
  } catch {
    return null;
  }
  if (!PRODUCT_OBJECT.test(objectPad) || IS_VARIANT.test(objectPad) || objectPad.includes("..")) return null;
  return { bucket: m[1], objectPad };
}

/** "products/x/abc.webp" + 640 -> "products/x/abc.w640.webp". */
export function variantObjectPad(objectPad: string, breedte: number): string {
  return objectPad.replace(/\.[A-Za-z0-9]+$/, "") + `.w${breedte}.webp`;
}

/** Download-URL van één variant (zonder token; zie het kopje bovenaan). */
export function variantUrl(bron: ProductFotoBron, breedte: number): string {
  return `https://${STORAGE_HOST}/v0/b/${bron.bucket}/o/${encodeURIComponent(variantObjectPad(bron.objectPad, breedte))}?alt=media`;
}

/** Alle variant-object-paden van een origineel, klein naar groot. */
export function alleVariantObjectPaden(objectPad: string): { breedte: number; objectPad: string }[] {
  return PRODUCT_VARIANT_BREEDTES.map((breedte) => ({ breedte, objectPad: variantObjectPad(objectPad, breedte) }));
}

/**
 * Doelbreedte (px) van een variant voor een origineel van `origineleBreedte` breed: nooit groter
 * dan het origineel (geen opschalen). Elke foto krijgt alle drie de bestanden, ook een kleine; de
 * srcset kan dan zonder te weten hoe breed het origineel is. Een bestand dat smaller uitvalt dan
 * zijn naam zegt is onschuldig: de browser kiest alleen op de beschrijving en schaalt zo nodig.
 */
export function variantDoelbreedte(breedte: number, origineleBreedte: number): number {
  const orig = Math.floor(Number(origineleBreedte));
  return orig > 0 ? Math.min(breedte, orig) : breedte;
}

// Het stukje van `sharp` dat hier nodig is, structureel getypeerd: zo importeert deze kern sharp niet
// zelf (de upload-routes en het backfill-script geven hun eigen sharp mee).
interface SharpBeeld {
  metadata(): Promise<{ width?: number; height?: number; orientation?: number }>;
  rotate(): SharpBeeld;
  resize(opties: { width: number; withoutEnlargement?: boolean }): SharpBeeld;
  webp(opties: { quality: number; effort?: number }): SharpBeeld;
  toBuffer(): Promise<Uint8Array>;
}
export type SharpFabriek = (invoer: Uint8Array, opties?: { failOn?: "none" }) => SharpBeeld;

/**
 * De varianten van één foto als bestanden in het geheugen: altijd alle breedtes, klein naar groot.
 * `rotate()` zet de EXIF-oriëntatie van oudere jpg-uploads in de pixels (het origineel draait de
 * browser zelf, een variant zonder EXIF zou anders op zijn kant staan). Gooit bij een onleesbare foto.
 */
export async function maakVariantBestanden(
  sharp: SharpFabriek,
  invoer: Uint8Array
): Promise<{ breedte: number; data: Uint8Array }[]> {
  const meta = await sharp(invoer, { failOn: "none" }).metadata();
  const omgedraaid = (meta.orientation ?? 1) >= 5; // 5-8 = 90 graden gedraaid: breedte en hoogte wisselen
  const origineleBreedte = (omgedraaid ? meta.height : meta.width) ?? 0;
  return Promise.all(
    PRODUCT_VARIANT_BREEDTES.map(async (breedte) => ({
      breedte,
      data: await sharp(invoer, { failOn: "none" })
        .rotate()
        .resize({ width: variantDoelbreedte(breedte, origineleBreedte), withoutEnlargement: true })
        .webp({ quality: PRODUCT_VARIANT_KWALITEIT, effort: 4 })
        .toBuffer(),
    }))
  );
}

/**
 * De srcset van een productkaart: "<url> 384w, <url> 640w, <url> 960w". Leeg ("") voor een foto die
 * niet in onze productmap staat: dan blijft het bij de gewone src.
 */
export function productFotoSrcSet(url: string | undefined | null): string {
  const bron = parseProductFotoUrl(url);
  if (!bron) return "";
  return PRODUCT_VARIANT_BREEDTES.map((b) => `${variantUrl(bron, b)} ${b}w`).join(", ");
}
